import { useForm } from '@tanstack/react-form';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { Check, Info, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { registerSchema } from 'shared';
import { toast } from 'sonner';
import { AuthPage } from '@/components/features/auth/auth-page';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { useTheme } from '@/contexts/theme-provider';
import {
  buildBackendUrlFromTenant,
  clearTenantId,
  createDynamicAuthClient,
  getTenantId,
  setBackendUrl,
} from '@/lib/better-auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/auth-context';

export const Route = createFileRoute('/register')({
  component: Register,
});

function Register() {
  const { t } = useTranslation();
  const { isAuthenticated, clearBackendUrl } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Tenant configuration state (separate from registration form)
  const [tenantIdInput, setTenantIdInput] = useState('');
  const [isValidatingBackend, setIsValidatingBackend] = useState(false);
  const [backendValidationResult, setBackendValidationResult] = useState<
    'success' | 'error' | null
  >(null);
  const [savedTenantId, setSavedTenantId] = useState<string>('');
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);

  // Load saved tenant ID on mount
  useEffect(() => {
    const saved = getTenantId();
    if (saved) {
      setSavedTenantId(saved);
      setTenantIdInput(saved);
    }
  }, []);

  const form = useForm({
    defaultValues: {
      name: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    onSubmit: async ({ value }) => {
      if (value.password !== value.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      setIsLoading(true);

      try {
        // Get the auth client with the current backend URL
        const authClient = createDynamicAuthClient();

        // Use better-auth's signUp.email method with username plugin
        const result = await authClient.signUp.email({
          email: value.email,
          password: value.password,
          name: value.name,
          username: value.username, // Username plugin will handle this
          callbackURL: '/home',
        });

        if (result.error) {
          // Handle better-auth errors
          const errorMessage = result.error.message || t('register.registrationError');
          toast.error(errorMessage, {
            description: t('register.tryAgain'),
          });
          return;
        }

        // If email verification is required, show a message
        if (result.data?.user?.emailVerified === false) {
          toast.info(t('register.registrationSuccess'), {
            description: t('register.verifyEmail'),
          });
        }

        // Use full page load to navigate to home
        // This ensures all clients are re-initialized with the new session
        window.location.href = '/home';
      } catch (_err) {
        toast.error(t('register.unexpectedError'));
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Test backend URL connectivity
  const handleTestBackend = async () => {
    if (!tenantIdInput.trim()) {
      setBackendValidationResult(null);
      return;
    }

    let url: string;
    if (isAdvancedMode) {
      // In advanced mode, use the input as full URL
      url = tenantIdInput.trim().replace(/\/$/, '');
    } else {
      // In tenant mode, build URL from tenant ID
      url = buildBackendUrlFromTenant(tenantIdInput.trim());
    }

    setIsValidatingBackend(true);
    setBackendValidationResult(null);

    try {
      const healthCheckUrl = `${url}/api/hello`;
      const response = await fetch(healthCheckUrl, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });

      if (response.ok) {
        setBackendValidationResult('success');
        toast.success('Backend connection successful', {
          description: 'Click "Save & Connect" to apply the configuration.',
        });
      } else {
        setBackendValidationResult('error');
        toast.error('Backend is not responding', {
          description: 'Please check your configuration and try again.',
        });
      }
    } catch {
      setBackendValidationResult('error');
      toast.error('Cannot connect to backend', {
        description: 'Please check your configuration and try again.',
      });
    } finally {
      setIsValidatingBackend(false);
    }
  };

  // Save tenant/backend URL and reload to apply changes
  const handleSaveBackendUrl = () => {
    if (backendValidationResult !== 'success') {
      toast.error('Please test the backend connection first');
      return;
    }

    const url = isAdvancedMode
      ? tenantIdInput.trim().replace(/\/$/, '')
      : buildBackendUrlFromTenant(tenantIdInput.trim());

    // Use setBackendUrl which handles localStorage and triggers page reload
    // This ensures all clients are re-initialized with the new backend URL
    setBackendUrl(url);
  };

  // Clear saved tenant/backend configuration
  const handleClearBackendUrl = () => {
    clearBackendUrl();
    clearTenantId();
    setTenantIdInput('');
    setSavedTenantId('');
    setBackendValidationResult(null);
    toast.success('Configuration cleared', {
      description: 'Using default backend. The page will reload.',
    });
  };

  if (isAuthenticated) {
    return <Navigate to="/home" />;
  }

  const { theme } = useTheme();
  const logoSrc =
    theme === 'dark'
      ? '/favicon-dark/android-chrome-192x192.png'
      : '/favicon/android-chrome-192x192.png';

  return (
    <AuthPage
      title="Create an account"
      description="Sign up to get started with OpexMES."
      logoSrc={logoSrc}
    >
      <form
        id="register-form"
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <FieldGroup className="space-y-4">
          <form.Field
            name="name"
            validators={{
              onBlur: ({ value }) => {
                const result = registerSchema.shape.name.safeParse(value);
                return result.success ? undefined : result.error.issues[0]?.message;
              },
              onChange: ({ value }) => {
                const result = registerSchema.shape.name.safeParse(value);
                return result.success ? undefined : result.error.issues[0]?.message;
              },
            }}
            children={(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Full Name</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="text"
                    autoComplete="name"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    placeholder="Enter your full name"
                    disabled={isLoading}
                  />
                  {isInvalid && field.state.meta.errors && (
                    <FieldError errors={field.state.meta.errors} />
                  )}
                </Field>
              );
            }}
          />
          <form.Field
            name="username"
            validators={{
              onBlur: ({ value }) => {
                const result = registerSchema.shape.username.safeParse(value);
                return result.success ? undefined : result.error.issues[0]?.message;
              },
              onChange: ({ value }) => {
                const result = registerSchema.shape.username.safeParse(value);
                return result.success ? undefined : result.error.issues[0]?.message;
              },
            }}
            children={(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Username</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="text"
                    autoComplete="username"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    placeholder="Enter your username (e.g. badge number)"
                    disabled={isLoading}
                  />
                  {isInvalid && field.state.meta.errors && (
                    <FieldError errors={field.state.meta.errors} />
                  )}
                </Field>
              );
            }}
          />
          <form.Field
            name="email"
            validators={{
              onBlur: ({ value }) => {
                const result = registerSchema.shape.email.safeParse(value);
                return result.success ? undefined : result.error.issues[0]?.message;
              },
              onChange: ({ value }) => {
                const result = registerSchema.shape.email.safeParse(value);
                return result.success ? undefined : result.error.issues[0]?.message;
              },
            }}
            children={(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Email</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="email"
                    autoComplete="email"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    placeholder="name@example.com"
                    disabled={isLoading}
                  />
                  {isInvalid && field.state.meta.errors && (
                    <FieldError errors={field.state.meta.errors} />
                  )}
                </Field>
              );
            }}
          />
          <form.Field
            name="password"
            validators={{
              onBlur: ({ value }) => {
                const result = registerSchema.shape.password.safeParse(value);
                return result.success ? undefined : result.error.issues[0]?.message;
              },
              onChange: ({ value }) => {
                const result = registerSchema.shape.password.safeParse(value);
                return result.success ? undefined : result.error.issues[0]?.message;
              },
            }}
            children={(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Password</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="password"
                    autoComplete="new-password"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    placeholder="Create a password (min 6 characters)"
                    disabled={isLoading}
                  />
                  {isInvalid && field.state.meta.errors && (
                    <FieldError errors={field.state.meta.errors} />
                  )}
                </Field>
              );
            }}
          />
          <form.Field
            name="confirmPassword"
            validators={{
              onBlur: ({ value, formApi }) => {
                if (!value) return 'Confirm password is required';
                if (value !== formApi.getFieldValue('password')) return 'Passwords do not match';
                return undefined;
              },
              onChange: ({ value, formApi }) => {
                if (!value) return undefined;
                if (value !== formApi.getFieldValue('password')) return 'Passwords do not match';
                return undefined;
              },
            }}
            children={(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Confirm Password</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="password"
                    autoComplete="new-password"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    placeholder="Confirm your password"
                    disabled={isLoading}
                  />
                  {isInvalid && field.state.meta.errors && (
                    <FieldError errors={field.state.meta.errors} />
                  )}
                </Field>
              );
            }}
          />
        </FieldGroup>

        {/* Tenant ID / Backend Configuration */}
        <div className="border-t pt-4">
          <details className="group">
            <summary className="flex cursor-pointer items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground list-none">
              <Info className="h-4 w-4" />
              <span>Tenant Configuration</span>
            </summary>
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsAdvancedMode(false);
                    setBackendValidationResult(null);
                  }}
                  className={`text-sm font-medium px-3 py-1 rounded-md transition-colors ${
                    !isAdvancedMode
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  Tenant ID
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAdvancedMode(true);
                    setBackendValidationResult(null);
                  }}
                  className={`text-sm font-medium px-3 py-1 rounded-md transition-colors ${
                    isAdvancedMode
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  Advanced (Full URL)
                </button>
              </div>

              {!isAdvancedMode ? (
                <>
                  <p className="text-xs text-muted-foreground">
                    Enter your organization's Tenant ID to connect to your backend server.
                  </p>
                  <div>
                    <FieldLabel htmlFor="tenant-id-input">Tenant ID</FieldLabel>
                    <div className="flex gap-2 mt-1">
                      <Input
                        id="tenant-id-input"
                        type="text"
                        value={tenantIdInput}
                        onChange={(e) => {
                          setTenantIdInput(e.target.value);
                          setBackendValidationResult(null);
                        }}
                        placeholder="e.g. demo, icbp, trias"
                        disabled={isValidatingBackend || isLoading}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleTestBackend}
                        disabled={!tenantIdInput.trim() || isValidatingBackend || isLoading}
                        title="Test connection"
                      >
                        {isValidatingBackend ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : backendValidationResult === 'success' ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : backendValidationResult === 'error' ? (
                          <X className="h-4 w-4 text-red-500" />
                        ) : (
                          <Info className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    Enter the full backend URL. Use this option if your server uses a custom domain.
                  </p>
                  <div>
                    <FieldLabel htmlFor="backend-url-input">Backend URL</FieldLabel>
                    <div className="flex gap-2 mt-1">
                      <Input
                        id="backend-url-input"
                        type="url"
                        value={tenantIdInput}
                        onChange={(e) => {
                          setTenantIdInput(e.target.value);
                          setBackendValidationResult(null);
                        }}
                        placeholder="https://your-server.workers.dev"
                        disabled={isValidatingBackend || isLoading}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleTestBackend}
                        disabled={!tenantIdInput.trim() || isValidatingBackend || isLoading}
                        title="Test connection"
                      >
                        {isValidatingBackend ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : backendValidationResult === 'success' ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : backendValidationResult === 'error' ? (
                          <X className="h-4 w-4 text-red-500" />
                        ) : (
                          <Info className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {savedTenantId && !isAdvancedMode && savedTenantId !== tenantIdInput && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted p-2 rounded">
                  <Info className="h-4 w-4" />
                  <span>
                    Current tenant: <span className="font-medium">{savedTenantId}</span>
                  </span>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSaveBackendUrl}
                  disabled={
                    backendValidationResult !== 'success' ||
                    (isAdvancedMode ? false : tenantIdInput.trim() === savedTenantId) ||
                    isLoading
                  }
                >
                  Save & Connect
                </Button>
                {savedTenantId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleClearBackendUrl}
                    disabled={isLoading}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </details>
        </div>

        <FieldGroup className="pt-2 space-y-4">
          <Field>
            <Button
              type="submit"
              form="register-form"
              disabled={isLoading}
              className="w-full"
              size="lg"
            >
              {isLoading ? 'Creating account...' : 'Create account'}
            </Button>
          </Field>
          <Field className="text-center">
            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-sm font-medium text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </Field>
        </FieldGroup>
      </form>
    </AuthPage>
  );
}
